import React, { type FC, type PropsWithChildren, useContext } from 'react';

import { InView } from 'react-intersection-observer';
import { useSiteMetadata } from '@lucid/hooks';

import ABTestContext from '../context/ab-test-context-singular';
import LayoutContext from '../../../components/context/layout-context';
import Redirect from '../../../components/redirect/redirect';
import type { Lucid } from '../../../types/types';
import useAbTest from '../hooks/use-ab-test';

/**
 * Renders a branch of an AB test
 *
 * @remarks
 * - Uses ABTestContext to determine the current test branch and trigger.
 * - Supports redirecting users or rendering children based on the test configuration.
 * - Falls back to rendering children for the default branch ('T_A') if no test is active.
 * - Will use singular AB Test Context by default
 * - If used within the scope of a global test, an ABTesting.Component.Base wrapper is not required.
 *
 * @returns The rendered branch element, a redirect, or nothing, depending on the AB test state and trigger.
 *
 * @example
 * ```tsx
 * <ABTesting.Component.Branch
 *   as="span"
 *   branch="T_B"
 *   testName="example-test"
 * >
 *   <span>Test Content</span>
 * </ABTesting.Component.Branch>
 */
const ABTestBranch: FC<PropsWithChildren<Lucid.ABTest.BranchProps>> = ({
  as: Element = 'div',
  branch,
  children,
  redirectPath,
  className,
  testName = '',
}) => {
  const { product } = useSiteMetadata();
  const { urlLangPrefix } = useContext(LayoutContext);
  const globalTest = useAbTest(testName);
  const test = useContext(ABTestContext);
  const currentTest = test?.testName ? test : globalTest;

  if (currentTest) {
    const { branch: currentBranch, trigger, handleOnTrigger, hasTriggerFired } = currentTest;

    return (
      branch === currentBranch &&
      (trigger === 'redirect' && hasTriggerFired && redirectPath ? (
        <Redirect
          key="redirectBranch"
          shouldPassQueryParams
          href={redirectPath}
          product={product}
          urlLangPrefix={urlLangPrefix}
        />
      ) : trigger === 'visible' ? (
        <InView
          key="branchInView"
          triggerOnce
          css={{ '&:empty': { display: 'none' } }}
          onChange={inView => inView && handleOnTrigger()}
          threshold={0.2}
        >
          <Element>{children}</Element>
        </InView>
      ) : (
        <Element className={className}>{children}</Element>
      ))
    );
  }

  if (branch === 'T_A') {
    return <Element className={className}>{children}</Element>;
  }
};

export { ABTestBranch };
export default ABTestBranch;
